<div class="asset-grade-card">

  <!-- Loading -->
  <div class="grade-header" *ngIf="loading">
    <div class="badge skeleton-badge"></div>
    <div class="header-text">
      <div class="skeleton-bar wide"></div>
      <div class="skeleton-bar"></div>
    </div>
  </div>

  <!-- Load failed. The card never shows an empty or zero grade instead. -->
  <div class="grade-error" *ngIf="!loading && loadError">
    <span class="error-text">{{ loadError }}</span>
    <button type="button" class="link-button" *ngIf="canRetry" (click)="load()">Retry</button>
  </div>

  <ng-container *ngIf="!loading && !loadError && response">

    <!--
      Nothing to show: no stored grade, and none generated in this session. Reason comes from the
      API, wording is ours. Keyed off activeRecord rather than hasGrade so that a grade generated
      from the button below replaces this block instead of leaving the preview invisible.
    -->
    <ng-container *ngIf="!hasRecordToShow">
      <div class="grade-empty">
        <div class="badge" [ngStyle]="{ 'background': badge(null).color, 'color': badge(null).textColor }">
          {{ badgeText(null) }}
        </div>
        <div class="header-text">
          <div class="title-row"><span class="title">Asset Grade</span></div>
          <div class="status-line">{{ noGradeText }}</div>
        </div>
      </div>

      <!--
        An open store that has never been graded can be graded on demand, so offer it here as
        well as on the graded card. Withheld for any other status: the API will not calculate a
        grade for one, so the button could only ever fail.
      -->
      <ng-container *ngIf="canGenerateFirstGrade">
        <div class="grade-notice" *ngIf="refreshNotice">{{ refreshNotice }}</div>
        <div class="refresh-error" *ngIf="refreshError">{{ refreshError }}</div>

        <div class="actions">
          <button type="button"
                  class="secondary-button"
                  [disabled]="generating"
                  (click)="generateFreshGrade()">
            <span class="refresh-icon" [class.spinning]="generating">&#10227;</span>
            {{ generating ? 'Generating…' : 'Generate Fresh Grade' }}
          </button>
        </div>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="hasRecordToShow">
      <div class="grade-header">
        <div class="badge"
             [ngStyle]="{ 'background': badge(activeRecord.overall?.grade).color,
                          'color': badge(activeRecord.overall?.grade).textColor }">
          {{ badgeText(activeRecord.overall?.grade) }}
        </div>

        <div class="header-text">
          <div class="title-row">
            <span class="title">Asset Grade</span>
            <span class="pill preview-pill" *ngIf="isPreviewActive">Preview</span>
            <span class="pill history-pill" *ngIf="isHistoryActive">History</span>
          </div>

          <div class="status-line" *ngIf="isPreviewActive">Generated just now &middot; not stored</div>
          <div class="status-line" *ngIf="isHistoryActive">
            Viewing {{ activeRecord.calculatedAt | date: 'MMM d, y' }} &middot; {{ label(activeRecord.trigger) }}
          </div>
          <div class="status-line" *ngIf="!isPreviewActive && !isHistoryActive">
            Last calculated {{ activeRecord.calculatedAt | date: 'MMM d, y' }}
          </div>
        </div>

        <!--
          Always shown when a stored grade exists, even with a single record. Hidden when the only
          record is an unsaved preview on a store that has never been graded: there is no history
          to step through, so the dropdown would open onto that one preview.
        -->
        <button type="button"
                class="history-button"
                *ngIf="hasStoredGrade"
                [class.open]="historyOpen"
                (click)="toggleHistory()"
                title="Grade history"
                aria-label="Grade history">
          <span class="history-icon">&#8635;</span>
        </button>

        <!-- Floats above the rows below; never resizes the card. -->
        <div class="history-dropdown" *ngIf="historyOpen">
          <button type="button"
                  class="history-row"
                  *ngIf="preview"
                  [class.active]="isActive(preview)"
                  (click)="selectRecord(preview)">
            <span class="row-badge"
                  [ngStyle]="{ 'background': badge(preview.overall?.grade).color,
                               'color': badge(preview.overall?.grade).textColor }">
              {{ badgeText(preview.overall?.grade) }}
            </span>
            <span class="row-text">
              <span class="row-date">Preview &middot; just now</span>
              <span class="row-trigger">Not stored</span>
            </span>
            <span class="row-check" *ngIf="isActive(preview)">&#10003;</span>
          </button>

          <button type="button"
                  class="history-row"
                  *ngFor="let record of historyRows"
                  [class.active]="isActive(record)"
                  (click)="selectRecord(record)">
            <span class="row-badge"
                  [ngStyle]="{ 'background': badge(record.overall?.grade).color,
                               'color': badge(record.overall?.grade).textColor }">
              {{ badgeText(record.overall?.grade) }}
            </span>
            <span class="row-text">
              <span class="row-date">
                {{ record.calculatedAt | date: 'MMM d, y' }}<span *ngIf="isCurrent(record)"> (current)</span>
              </span>
              <span class="row-trigger">{{ label(record.trigger) }}</span>
            </span>
            <span class="row-check" *ngIf="isActive(record)">&#10003;</span>
          </button>
        </div>
      </div>

      <!-- Five subscores, when this record has them. -->
      <div class="subscores" *ngIf="subscores.length">
        <div class="subscore-row" *ngFor="let subscore of subscores">
          <img class="subscore-icon" [src]="subscore.icon" alt="" aria-hidden="true" />
          <span class="subscore-label-group">
            <span class="subscore-label">{{ subscore.label }}</span>
            <!--
              Same circled-i affordance as the Full Breakdown, but the bubble is a plain element
              shown on :hover rather than one built in JS and appended to <body>. Nothing to
              mis-wire, and it is clipped by nothing: the history dropdown already floats out of
              this card the same way.
            -->
            <span class="info-wrap">
              <svg class="info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none"
                   role="img" [attr.aria-label]="subscore.label + ' definition'">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M3.25 12C3.25 7.16751 7.16751 3.25 12 3.25C16.8325 3.25 20.75 7.16751 20.75 12C20.75 16.8325 16.8325 20.75 12 20.75C7.16751 20.75 3.25 16.8325 3.25 12ZM13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7C12.5523 7 13 7.44772 13 8ZM12 10.75C12.4142 10.75 12.75 11.0858 12.75 11.5V16.5C12.75 16.9142 12.4142 17.25 12 17.25C11.5858 17.25 11.25 16.9142 11.25 16.5V11.5C11.25 11.0858 11.5858 10.75 12 10.75Z" fill="#50C878"/>
              </svg>
              <span class="info-bubble" role="tooltip" [innerHTML]="subscore.tooltip"></span>
            </span>
          </span>
          <span class="subscore-badge"
                [ngStyle]="{ 'background': badge(subscore.grade).color,
                             'color': badge(subscore.grade).textColor }">
            {{ badgeText(subscore.grade) }}
          </span>
        </div>
      </div>

      <!-- Older records stored only the overall grade, so say so rather than showing blanks. -->
      <div class="detail-missing" *ngIf="detailUnavailable">
        Subscore detail was not saved for this calculation. Generate a fresh grade to see the full breakdown.
      </div>

      <div class="grade-notice" *ngIf="refreshNotice">{{ refreshNotice }}</div>
      <div class="refresh-error" *ngIf="refreshError">{{ refreshError }}</div>

      <div class="actions">
        <button type="button"
                class="secondary-button"
                [disabled]="generating"
                (click)="generateFreshGrade()">
          <span class="refresh-icon" [class.spinning]="generating">&#10227;</span>
          {{ generating ? 'Generating…' : 'Generate Fresh Grade' }}
        </button>

        <button type="button"
                class="primary-button"
                [disabled]="detailUnavailable"
                [title]="detailUnavailable ? 'No detail stored for this record' : ''"
                (click)="openFullBreakdown()">
          View Full Breakdown &rarr;
        </button>
      </div>
    </ng-container>
  </ng-container>
</div>
